import { Box, Stack, Typography, Slider, Alert, useTheme } from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import type { Data, Layout, Shape } from "plotly.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MassTimeSeries,
  ScaleReadingLookup,
  useGetMassCandidateEventsQuery,
} from "src/store/api/generatedApi";
import {
  computeLinearDtick,
  computeMeasurementBounds,
  findWeightAt,
  groupScaleWeightSeriesByArea,
  resolveTargetArea,
  ScaleWeightSeriesData,
} from "components/identifyEventsTool/tool/ScaleWeightsChart.utils";
import { useMassData } from "../contexts/MassDataContext";
import {
  computeVisibleEnd,
  computeVisibleStart,
  findNeighbouringDropBounds,
  zoomAroundEvent,
} from "./massAdjustmentRange";
import {
  DEFAULT_MASS_CHART_ZOOM_INDEX,
  MASS_CHART_ZOOM_FACTORS,
  MassChartZoomControls,
} from "./MassChartZoomControls";
import { TimeAxisLayout } from "./timeAxisLayout";

dayjs.extend(utc);

export interface MassChangeValues {
  mass: string | null;
  initialMass: number | null;
  finalMass: number | null;
}

type MassAdjustmentChartProps = Readonly<{
  eventId: string;
  eventStart: Dayjs;
  eventEnd: Dayjs;
  massEventStart: Dayjs | null;
  massEventEnd: Dayjs | null;
  eventMass: string | null;
  massEventMass: string | null;
  area: string | null;
  storedInitialMass: number | null;
  storedFinalMass: number | null;
  pairedEventStart?: Dayjs | null;
  pairedEventEnd?: Dayjs | null;
  playheadTime?: Dayjs;
  onMassChange: (values: MassChangeValues) => void;
  onAutoSync?: (values: MassChangeValues) => void;
  onTimeAxisChange?: (timeAxis: TimeAxisLayout | null) => void;
}>;

const PADDING_SECONDS = 20;
const MAX_MEASUREMENT_LOOKBACK_SECONDS = 180;
const MAX_NEIGHBOUR_REACH_SECONDS = 90;
const CHART_HEIGHT = 220;
const PLOT_MARGIN = { l: 45, r: 10, t: 10, b: 35 };
const MASS_GRID_STEP = 0.05;

const snapToGrid = (value: number): number =>
  Math.round(value / MASS_GRID_STEP) * MASS_GRID_STEP;

const snapFloorToGrid = (value: number): number =>
  Math.floor(value / MASS_GRID_STEP) * MASS_GRID_STEP;

const snapCeilToGrid = (value: number): number =>
  Math.ceil(value / MASS_GRID_STEP) * MASS_GRID_STEP;

const buildScaleWeightTrace = ({
  seriesData,
  area,
  traceColor,
}: {
  seriesData: ScaleWeightSeriesData;
  area: string;
  traceColor: string;
}): Partial<Data> => ({
  x: seriesData.x.map((d) => d.toISOString().slice(0, 19)),
  y: seriesData.y,
  type: "scatter" as const,
  mode: "lines" as const,
  name: area,
  line: { color: traceColor, width: 2 },
  hovertemplate: `<b>%{fullData.name}</b><br>Time (UTC): %{x}<br>Weight: %{y:.2f} t<extra></extra>`,
});

interface BuildBoundaryShapesParams {
  mergedStart: Dayjs;
  mergedEnd: Dayjs;
  massEventStart: Dayjs | null;
  massEventEnd: Dayjs | null;
  massEventInitialWeight: number | null;
  massEventMass: number | null;
  initialMass: number;
  finalMass: number;
  pairedEventStart?: Dayjs | null;
  pairedEventEnd?: Dayjs | null;
  colors: {
    mergedBoundary: string;
    massEventFill: string;
    initialMassLine: string;
    finalMassLine: string;
    selectionFill: string;
    pairedFill: string;
  };
}

const buildBoundaryShapes = ({
  mergedStart,
  mergedEnd,
  massEventStart,
  massEventEnd,
  massEventInitialWeight,
  massEventMass,
  initialMass,
  finalMass,
  pairedEventStart,
  pairedEventEnd,
  colors,
}: BuildBoundaryShapesParams): Partial<Shape>[] => {
  const hasMassEvent =
    massEventStart &&
    massEventEnd &&
    massEventInitialWeight !== null &&
    massEventMass !== null;

  const massEventComputedFinalWeight =
    massEventInitialWeight !== null && massEventMass !== null
      ? massEventInitialWeight + massEventMass
      : null;

  const massEventRect: Partial<Shape>[] =
    hasMassEvent && massEventComputedFinalWeight !== null
      ? [
          {
            type: "rect",
            x0: massEventStart.utc().toISOString(),
            x1: massEventEnd.utc().toISOString(),
            y0: Math.min(massEventComputedFinalWeight, massEventInitialWeight),
            y1: Math.max(massEventComputedFinalWeight, massEventInitialWeight),
            fillcolor: colors.massEventFill,
            line: { color: "rgba(255, 152, 0, 0.5)", width: 1, dash: "dot" },
          },
        ]
      : [];

  const pairedEventShapes: Partial<Shape>[] =
    pairedEventStart && pairedEventEnd
      ? [
          {
            type: "line",
            x0: pairedEventStart.utc().toISOString(),
            x1: pairedEventStart.utc().toISOString(),
            yref: "paper" as const,
            y0: 0,
            y1: 1,
            line: { color: colors.mergedBoundary, width: 2, dash: "dash" },
          },
          {
            type: "line",
            x0: pairedEventEnd.utc().toISOString(),
            x1: pairedEventEnd.utc().toISOString(),
            yref: "paper" as const,
            y0: 0,
            y1: 1,
            line: { color: colors.mergedBoundary, width: 2, dash: "dash" },
          },
          {
            type: "rect",
            x0: pairedEventStart.utc().toISOString(),
            x1: pairedEventEnd.utc().toISOString(),
            y0: Math.min(finalMass, initialMass),
            y1: Math.max(finalMass, initialMass),
            fillcolor: colors.pairedFill,
            line: { width: 0 },
          },
        ]
      : [];

  return [
    ...massEventRect,
    ...pairedEventShapes,
    {
      type: "line",
      x0: mergedStart.utc().toISOString(),
      x1: mergedStart.utc().toISOString(),
      yref: "paper",
      y0: 0,
      y1: 1,
      line: { color: colors.mergedBoundary, width: 2, dash: "dash" },
    },
    {
      type: "line",
      x0: mergedEnd.utc().toISOString(),
      x1: mergedEnd.utc().toISOString(),
      yref: "paper",
      y0: 0,
      y1: 1,
      line: { color: colors.mergedBoundary, width: 2, dash: "dash" },
    },
    {
      type: "line",
      xref: "paper",
      x0: 0,
      x1: 1,
      y0: initialMass,
      y1: initialMass,
      line: { color: colors.initialMassLine, width: 2, dash: "solid" },
    },
    {
      type: "line",
      xref: "paper",
      x0: 0,
      x1: 1,
      y0: finalMass,
      y1: finalMass,
      line: { color: colors.finalMassLine, width: 2, dash: "solid" },
    },
    {
      type: "rect",
      x0: mergedStart.utc().toISOString(),
      x1: mergedEnd.utc().toISOString(),
      y0: Math.min(finalMass, initialMass),
      y1: Math.max(finalMass, initialMass),
      fillcolor: colors.selectionFill,
      line: { width: 0 },
    },
  ];
};

type MassAdjustmentChartInnerProps = Readonly<{
  data: MassTimeSeries;
  eventId: string;
  eventStart: Dayjs;
  eventEnd: Dayjs;
  massEventStart: Dayjs | null;
  massEventEnd: Dayjs | null;
  eventMass: string | null;
  massEventMass: string | null;
  area: string | null;
  scaleReadingLookup: ScaleReadingLookup;
  defaultVisibleStart: Dayjs;
  defaultVisibleEnd: Dayjs;
  canZoomOut: boolean;
  storedInitialMass: number | null;
  storedFinalMass: number | null;
  pairedEventStart?: Dayjs | null;
  pairedEventEnd?: Dayjs | null;
  playheadTime?: Dayjs;
  onMassChange: (values: MassChangeValues) => void;
  // Materialising a derived default onto the event isn't a reviewer edit.
  // When provided, it receives that write so the caller can commit it
  // without recording an undo/redo step.
  onAutoSync?: (values: MassChangeValues) => void;
  onTimeAxisChange?: (timeAxis: TimeAxisLayout | null) => void;
}>;

const MassAdjustmentChartInner = ({
  data,
  eventId,
  eventStart,
  eventEnd,
  massEventStart,
  massEventEnd,
  eventMass,
  massEventMass,
  area,
  scaleReadingLookup,
  defaultVisibleStart,
  defaultVisibleEnd,
  canZoomOut,
  storedInitialMass,
  storedFinalMass,
  pairedEventStart,
  pairedEventEnd,
  playheadTime,
  onMassChange,
  onAutoSync,
  onTimeAxisChange,
}: MassAdjustmentChartInnerProps) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const formatUnits = useUnitsFormatter(false);
  const { drop_areas: dropAreas } = useScrapEyeMetadata();

  const colors = useMemo(
    () => ({
      mergedBoundary: theme.palette.text.secondary,
      massEventFill: "rgba(255, 152, 0, 0.2)",
      traceColor: theme.palette.info.main,
      initialMassLine: theme.palette.success.main,
      finalMassLine: theme.palette.error.main,
      playhead: theme.palette.error.main,
      selectionFill: "rgba(33, 150, 243, 0.2)",
      pairedFill: "rgba(158, 158, 158, 0.12)",
      gridColor: theme.palette.divider,
      tickColor: theme.palette.text.secondary,
    }),
    [theme]
  );

  const [zoomIndex, setZoomIndex] = useState(DEFAULT_MASS_CHART_ZOOM_INDEX);
  const [zoomedEventId, setZoomedEventId] = useState(eventId);
  if (eventId !== zoomedEventId) {
    setZoomedEventId(eventId);
    setZoomIndex(DEFAULT_MASS_CHART_ZOOM_INDEX);
  }
  const { visibleStart, visibleEnd } = useMemo(
    () =>
      zoomAroundEvent({
        eventStart,
        eventEnd,
        defaultStart: defaultVisibleStart,
        defaultEnd: defaultVisibleEnd,
        zoomFactor: MASS_CHART_ZOOM_FACTORS[zoomIndex] ?? 1,
      }),
    [eventStart, eventEnd, defaultVisibleStart, defaultVisibleEnd, zoomIndex]
  );

  const { grouped: groupedData, orderedAreas: areasWithScaleWeights } = useMemo(
    () => groupScaleWeightSeriesByArea(data.series),
    [data.series]
  );

  const targetArea = resolveTargetArea({
    area,
    orderedAreas: areasWithScaleWeights,
    dropAreas,
  });
  const seriesData = targetArea ? groupedData[targetArea] : undefined;
  const hasScaleWeightTrace =
    Boolean(targetArea) && seriesData !== undefined && seriesData.x.length > 0;

  const plotContainerRef = useRef<HTMLDivElement>(null);
  const visibleStartMs = visibleStart.valueOf();
  const visibleEndMs = visibleEnd.valueOf();
  useEffect(() => {
    const plotContainer = plotContainerRef.current;
    if (!onTimeAxisChange || !hasScaleWeightTrace || !plotContainer) return;
    onTimeAxisChange({
      start: dayjs.utc(visibleStartMs),
      end: dayjs.utc(visibleEndMs),
      leftInsetPx: plotContainer.offsetLeft + PLOT_MARGIN.l,
      rightInsetPx: PLOT_MARGIN.r,
    });
    return () => {
      onTimeAxisChange(null);
    };
  }, [onTimeAxisChange, hasScaleWeightTrace, visibleStartMs, visibleEndMs]);

  const massEventInitialWeight = useMemo(
    () =>
      massEventStart
        ? findWeightAt(seriesData, massEventStart, scaleReadingLookup)
        : null,
    [seriesData, massEventStart, scaleReadingLookup]
  );

  const massEventComputedFinalWeight = useMemo(() => {
    if (massEventInitialWeight !== null && massEventMass !== null) {
      return snapToGrid(massEventInitialWeight + Number(massEventMass));
    }
    return null;
  }, [massEventInitialWeight, massEventMass]);

  const massStart = massEventStart ?? eventStart;
  const massEnd = massEventEnd ?? eventEnd;

  const derivedInitialMass = useMemo(
    () => findWeightAt(seriesData, massStart, scaleReadingLookup),
    [seriesData, massStart, scaleReadingLookup]
  );

  const derivedFinalMass = useMemo(() => {
    if (derivedInitialMass !== null && eventMass !== null) {
      return snapToGrid(derivedInitialMass + Number(eventMass));
    }
    return findWeightAt(seriesData, massEnd, scaleReadingLookup);
  }, [derivedInitialMass, eventMass, seriesData, massEnd, scaleReadingLookup]);

  const defaultInitialMass = storedInitialMass ?? derivedInitialMass;
  const defaultFinalMass = storedFinalMass ?? derivedFinalMass;

  const [initialMass, setInitialMass] = useState(defaultInitialMass);
  const [finalMass, setFinalMass] = useState(defaultFinalMass);

  // A caller that commits mass changes immediately produces a new event
  // object on every call, which can give these callbacks a new identity
  // each time. Reading them through refs keeps this effect's dependencies
  // limited to defaultInitialMass/defaultFinalMass, so it only re-fires on
  // a genuine value change.
  const onMassChangeRef = useRef(onMassChange);
  onMassChangeRef.current = onMassChange;
  const onAutoSyncRef = useRef(onAutoSync);
  onAutoSyncRef.current = onAutoSync;

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- syncs draggable mass from derived defaults
    setInitialMass(defaultInitialMass);
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- syncs draggable mass from derived defaults
    setFinalMass(defaultFinalMass);

    const notifyAutoSync = onAutoSyncRef.current ?? onMassChangeRef.current;
    if (defaultInitialMass !== null && defaultFinalMass !== null) {
      const newDroppedMass =
        Math.round((defaultFinalMass - defaultInitialMass) * 100) / 100;
      notifyAutoSync({
        mass: newDroppedMass > 0 ? newDroppedMass.toFixed(2) : null,
        initialMass: defaultInitialMass,
        finalMass: defaultFinalMass,
      });
    } else {
      notifyAutoSync({
        mass: null,
        initialMass: null,
        finalMass: null,
      });
    }
  }, [defaultInitialMass, defaultFinalMass]);

  const visibleYRange = useMemo((): [number, number] | undefined => {
    if (!seriesData) return undefined;
    const visibleStartMs = visibleStart.valueOf();
    const visibleEndMs = visibleEnd.valueOf();

    const visibleYValues = seriesData.x
      .map((xVal, i) => ({ x: xVal, y: seriesData.y[i] }))
      .filter(
        ({ x, y }) =>
          y !== undefined &&
          x.getTime() >= visibleStartMs &&
          x.getTime() <= visibleEndMs
      )
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map(({ y }) => y!);

    if (visibleYValues.length === 0) return undefined;

    const minY = Math.min(...visibleYValues);
    const maxY = Math.max(...visibleYValues);
    const padding = Math.max(0.5, (maxY - minY) * 0.15);
    return [minY - padding, maxY + padding];
  }, [seriesData, visibleStart, visibleEnd]);

  // The sliders clamp values outside their range, so their range includes the
  // selected levels.
  const selectedLevels = [initialMass, finalMass].filter(
    (level): level is number => level !== null
  );
  const sliderMin = snapFloorToGrid(
    Math.min(visibleYRange?.[0] ?? 0, ...selectedLevels)
  );
  const sliderMax = snapCeilToGrid(
    Math.max(visibleYRange?.[1] ?? 100, ...selectedLevels)
  );

  const droppedMass = useMemo(() => {
    if (initialMass === null || finalMass === null) return null;
    const mass = finalMass - initialMass;
    return mass > 0 ? Math.round(mass * 100) / 100 : null;
  }, [initialMass, finalMass]);

  // onChange fires continuously while dragging; only the value at drag end
  // is worth its own undo/redo entry, so the actual commit happens in
  // onChangeCommitted below.
  const handleInitialMassChange = useCallback(
    (_: Event, value: number | number[]) => {
      const newValue = Array.isArray(value) ? value[0] : value;
      if (newValue === undefined) return;
      const clampedValue =
        finalMass !== null ? Math.min(newValue, finalMass) : newValue;
      setInitialMass(clampedValue);
    },
    [finalMass]
  );

  const handleInitialMassChangeCommitted = useCallback(
    (_: React.SyntheticEvent | Event, value: number | number[]) => {
      const newValue = Array.isArray(value) ? value[0] : value;
      if (newValue === undefined) return;
      const clampedValue =
        finalMass !== null ? Math.min(newValue, finalMass) : newValue;
      const newDroppedMass =
        finalMass !== null
          ? Math.round((finalMass - clampedValue) * 100) / 100
          : null;
      onMassChange({
        mass:
          newDroppedMass !== null && newDroppedMass > 0
            ? newDroppedMass.toFixed(2)
            : null,
        initialMass: clampedValue,
        finalMass,
      });
    },
    [finalMass, onMassChange]
  );

  const handleFinalMassChange = useCallback(
    (_: Event, value: number | number[]) => {
      const newValue = Array.isArray(value) ? value[0] : value;
      if (newValue === undefined) return;
      const clampedValue =
        initialMass !== null ? Math.max(newValue, initialMass) : newValue;
      setFinalMass(clampedValue);
    },
    [initialMass]
  );

  const handleFinalMassChangeCommitted = useCallback(
    (_: React.SyntheticEvent | Event, value: number | number[]) => {
      const newValue = Array.isArray(value) ? value[0] : value;
      if (newValue === undefined) return;
      const clampedValue =
        initialMass !== null ? Math.max(newValue, initialMass) : newValue;
      const newDroppedMass =
        initialMass !== null
          ? Math.round((clampedValue - initialMass) * 100) / 100
          : null;
      onMassChange({
        mass:
          newDroppedMass !== null && newDroppedMass > 0
            ? newDroppedMass.toFixed(2)
            : null,
        initialMass,
        finalMass: clampedValue,
      });
    },
    [initialMass, onMassChange]
  );

  const plotData: Partial<Data>[] = useMemo(
    () =>
      seriesData && targetArea
        ? [
            buildScaleWeightTrace({
              seriesData,
              area: targetArea,
              traceColor: colors.traceColor,
            }),
          ]
        : [],
    [seriesData, targetArea, colors.traceColor]
  );

  const boundaryShapes = useMemo(
    () =>
      buildBoundaryShapes({
        mergedStart: eventStart,
        mergedEnd: eventEnd,
        massEventStart,
        massEventEnd,
        massEventInitialWeight,
        massEventMass: massEventMass !== null ? Number(massEventMass) : null,
        initialMass: initialMass ?? 0,
        finalMass: finalMass ?? 0,
        pairedEventStart,
        pairedEventEnd,
        colors: {
          mergedBoundary: colors.mergedBoundary,
          massEventFill: colors.massEventFill,
          initialMassLine: colors.initialMassLine,
          finalMassLine: colors.finalMassLine,
          selectionFill: colors.selectionFill,
          pairedFill: colors.pairedFill,
        },
      }),
    [
      eventStart,
      eventEnd,
      massEventStart,
      massEventEnd,
      massEventInitialWeight,
      massEventMass,
      initialMass,
      finalMass,
      pairedEventStart,
      pairedEventEnd,
      colors,
    ]
  );

  const playheadShapes = useMemo((): Partial<Shape>[] => {
    if (
      !playheadTime ||
      playheadTime.isBefore(visibleStart) ||
      playheadTime.isAfter(visibleEnd)
    ) {
      return [];
    }
    const playheadX = playheadTime.utc().toISOString();
    return [
      {
        type: "line",
        x0: playheadX,
        x1: playheadX,
        yref: "paper",
        y0: 0,
        y1: 1,
        line: { color: colors.playhead, width: 2 },
      },
    ];
  }, [playheadTime, visibleStart, visibleEnd, colors.playhead]);

  const layout: Partial<Layout> = useMemo(
    () => ({
      autosize: true,
      height: CHART_HEIGHT,
      margin: PLOT_MARGIN,
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      xaxis: {
        type: "date",
        range: [
          visibleStart.utc().toISOString(),
          visibleEnd.utc().toISOString(),
        ],
        showgrid: false,
        gridcolor: colors.gridColor,
        tickformat: "%H:%M:%S",
        tickfont: { size: 10, color: colors.tickColor },
      },
      yaxis: {
        showticklabels: true,
        showgrid: false,
        gridcolor: colors.gridColor,
        fixedrange: true,
        range: visibleYRange,
        tickfont: { size: 10, color: colors.tickColor },
        tickmode: "linear" as const,
        dtick: computeLinearDtick(visibleYRange),
        title: {
          text: formatUnits("mass"),
          font: { size: 10, color: colors.tickColor },
        },
      },
      showlegend: false,
      hovermode: "closest",
      dragmode: false as const,
      shapes: [...boundaryShapes, ...playheadShapes],
    }),
    [
      visibleStart,
      visibleEnd,
      visibleYRange,
      boundaryShapes,
      playheadShapes,
      formatUnits,
      colors,
    ]
  );

  if (!hasScaleWeightTrace) {
    return (
      <Alert severity="info" sx={{ my: 1 }}>
        {t("noScaleWeightDataForArea")}
      </Alert>
    );
  }

  const hasMassEventData =
    massEventStart !== null &&
    massEventEnd !== null &&
    massEventInitialWeight !== null &&
    massEventComputedFinalWeight !== null;

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="stretch"
        sx={{ position: "relative" }}
      >
        <Stack spacing={0} sx={{ alignItems: "center" }}>
          <Stack direction="row" spacing={0.25} sx={{ mb: 0.25 }}>
            <Typography
              variant="caption"
              color="success.main"
              sx={{
                fontSize: "0.5rem",
                fontWeight: 600,
                width: 28,
                textAlign: "center",
              }}
            >
              {initialMass !== null ? initialMass.toFixed(1) : "—"}
            </Typography>
            <Typography
              variant="caption"
              color="error.main"
              sx={{
                fontSize: "0.5rem",
                fontWeight: 600,
                width: 28,
                textAlign: "center",
              }}
            >
              {finalMass !== null ? finalMass.toFixed(1) : "—"}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0} sx={{ height: CHART_HEIGHT - 50 }}>
            <Slider
              orientation="vertical"
              size="small"
              value={initialMass ?? sliderMax}
              min={sliderMin}
              max={sliderMax}
              step={MASS_GRID_STEP}
              marks={
                massEventInitialWeight !== null
                  ? [{ value: massEventInitialWeight, label: "" }]
                  : []
              }
              onChange={handleInitialMassChange}
              onChangeCommitted={handleInitialMassChangeCommitted}
              sx={{
                color: "success.main",
                "& .MuiSlider-thumb": { width: 14, height: 14 },
                "& .MuiSlider-track": { display: "none" },
                "& .MuiSlider-rail": { opacity: 0.3 },
                "& .MuiSlider-mark": {
                  width: 8,
                  height: 2,
                  bgcolor: "warning.main",
                  transform: "translateX(-50%)",
                },
              }}
            />
            <Slider
              orientation="vertical"
              size="small"
              value={finalMass ?? sliderMin}
              min={sliderMin}
              max={sliderMax}
              step={MASS_GRID_STEP}
              marks={
                massEventComputedFinalWeight !== null
                  ? [{ value: massEventComputedFinalWeight, label: "" }]
                  : []
              }
              onChange={handleFinalMassChange}
              onChangeCommitted={handleFinalMassChangeCommitted}
              sx={{
                color: "error.main",
                "& .MuiSlider-thumb": { width: 14, height: 14 },
                "& .MuiSlider-track": { display: "none" },
                "& .MuiSlider-rail": { opacity: 0.3 },
                "& .MuiSlider-mark": {
                  width: 8,
                  height: 2,
                  bgcolor: "warning.main",
                  transform: "translateX(-50%)",
                },
              }}
            />
          </Stack>
        </Stack>

        <Box ref={plotContainerRef} sx={{ flex: 1, position: "relative" }}>
          <Plot
            data={plotData}
            layout={layout}
            config={{
              displayModeBar: false,
              responsive: true,
              scrollZoom: false,
            }}
            style={{ width: "100%" }}
            useResizeHandler
          />
        </Box>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        useFlexGap
        sx={{ px: 1, flexWrap: "wrap" }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 16,
              height: 0,
              borderTop: (theme) => `2px solid ${theme.palette.info.main}`,
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.6rem" }}
          >
            {t("scaleWeight")}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 16,
              height: 0,
              borderTop: (theme) =>
                `2px dashed ${theme.palette.text.secondary}`,
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.6rem" }}
          >
            {t("labelledVisualDrop")}
          </Typography>
        </Stack>
        {hasMassEventData ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 12,
                height: 8,
                bgcolor: "warning.main",
                opacity: 0.2,
                border: (theme) => `1px dotted ${theme.palette.warning.main}`,
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.6rem" }}
            >
              {t("detectedDropFromMass")}
            </Typography>
          </Stack>
        ) : null}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 16,
              height: 0,
              borderTop: (theme) => `2px solid ${theme.palette.success.main}`,
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.6rem" }}
          >
            {t("initialMass")}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 16,
              height: 0,
              borderTop: (theme) => `2px solid ${theme.palette.error.main}`,
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.6rem" }}
          >
            {t("finalMass")}
          </Typography>
        </Stack>
        {canZoomOut ? (
          <Box sx={{ ml: "auto" }}>
            <MassChartZoomControls
              zoomIndex={zoomIndex}
              onZoomIndexChange={setZoomIndex}
            />
          </Box>
        ) : null}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ py: 1, px: 2, bgcolor: "action.hover", borderRadius: 1 }}
      >
        <Typography variant="body2" fontWeight={600}>
          {t("droppedMass")}:
        </Typography>
        <Typography
          variant="h6"
          fontWeight={700}
          color={droppedMass !== null ? "primary.main" : "text.disabled"}
        >
          {droppedMass !== null
            ? `${droppedMass.toFixed(2)} ${formatUnits("mass")}`
            : "—"}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const MassAdjustmentChart = ({
  eventId,
  eventStart,
  eventEnd,
  massEventStart,
  massEventEnd,
  eventMass,
  massEventMass,
  area,
  storedInitialMass,
  storedFinalMass,
  pairedEventStart,
  pairedEventEnd,
  playheadTime,
  onMassChange,
  onAutoSync,
  onTimeAxisChange,
}: MassAdjustmentChartProps) => {
  const { t } = useTenantTranslation();
  const {
    data: fullData,
    isLoading,
    isError,
    filterToWindow,
    queryArgs,
  } = useMassData();
  const { drop_areas: dropAreas, scale_reading_lookup: scaleReadingLookup } =
    useScrapEyeMetadata();
  const hasWideMassAdjustmentView = useIsBehindFeatureFlag(
    "mass_adjustment_wide_view"
  );
  const { data: detectedDrops, isLoading: isLoadingDetectedDrops } =
    useGetMassCandidateEventsQuery(queryArgs, {
      skip: !hasWideMassAdjustmentView || !fullData,
    });

  const scaleWeightsByArea = useMemo(
    () => (fullData ? groupScaleWeightSeriesByArea(fullData.series) : null),
    [fullData]
  );
  const targetArea = scaleWeightsByArea
    ? resolveTargetArea({
        area,
        orderedAreas: scaleWeightsByArea.orderedAreas,
        dropAreas,
      })
    : area;

  const { measurementStart, measurementEnd } = useMemo(() => {
    if (!scaleWeightsByArea)
      return { measurementStart: null, measurementEnd: null };
    return computeMeasurementBounds({
      grouped: scaleWeightsByArea.grouped,
      orderedAreas: scaleWeightsByArea.orderedAreas,
      dropAreas,
      area,
      firstMoment: massEventStart?.isBefore(eventStart)
        ? massEventStart
        : eventStart,
      lastMoment: massEventEnd?.isAfter(eventEnd) ? massEventEnd : eventEnd,
      maxLookbackSeconds: MAX_MEASUREMENT_LOOKBACK_SECONDS,
      lookup: scaleReadingLookup,
    });
  }, [
    scaleWeightsByArea,
    dropAreas,
    scaleReadingLookup,
    area,
    eventStart,
    eventEnd,
    massEventStart,
    massEventEnd,
  ]);

  const { previousDropBound, nextDropBound } = useMemo(
    () =>
      hasWideMassAdjustmentView
        ? findNeighbouringDropBounds({
            candidateEvents: detectedDrops?.candidate_events ?? [],
            area: targetArea,
            eventStart,
            eventEnd,
            massEventStart,
            massEventEnd,
            maxReachSeconds: MAX_NEIGHBOUR_REACH_SECONDS,
          })
        : { previousDropBound: null, nextDropBound: null },
    [
      hasWideMassAdjustmentView,
      detectedDrops?.candidate_events,
      targetArea,
      eventStart,
      eventEnd,
      massEventStart,
      massEventEnd,
    ]
  );

  const defaultVisibleStart = useMemo(
    () =>
      computeVisibleStart({
        eventStart,
        eventEnd,
        pairedEventStart,
        massEventStart,
        measurementStart,
        previousDropBound,
        paddingSeconds: PADDING_SECONDS,
      }),
    [
      eventStart,
      eventEnd,
      pairedEventStart,
      massEventStart,
      measurementStart,
      previousDropBound,
    ]
  );
  const defaultVisibleEnd = useMemo(
    () =>
      computeVisibleEnd({
        eventStart,
        eventEnd,
        pairedEventEnd,
        massEventEnd,
        measurementEnd,
        nextDropBound,
        paddingSeconds: PADDING_SECONDS,
      }),
    [
      eventStart,
      eventEnd,
      pairedEventEnd,
      massEventEnd,
      measurementEnd,
      nextDropBound,
    ]
  );

  // The derived initial and final masses read this series. Filtering it to the
  // widest zoom stops them from changing when the reviewer zooms.
  const data = useMemo(() => {
    const widestWindow = zoomAroundEvent({
      eventStart,
      eventEnd,
      defaultStart: defaultVisibleStart,
      defaultEnd: defaultVisibleEnd,
      zoomFactor: hasWideMassAdjustmentView
        ? Math.max(...MASS_CHART_ZOOM_FACTORS)
        : 1,
    });
    return filterToWindow(widestWindow.visibleStart, widestWindow.visibleEnd);
  }, [
    hasWideMassAdjustmentView,
    filterToWindow,
    eventStart,
    eventEnd,
    defaultVisibleStart,
    defaultVisibleEnd,
  ]);

  if (isLoading || isLoadingDetectedDrops) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: CHART_HEIGHT,
          bgcolor: "action.hover",
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t("loadingScaleWeights")}
        </Typography>
      </Box>
    );
  }

  if (isError || !fullData || fullData.series.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 1 }}>
        {t("noScaleWeightData")}
      </Alert>
    );
  }

  if (!data || data.series.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 1 }}>
        {t("noScaleWeightDataForArea")}
      </Alert>
    );
  }

  return (
    <MassAdjustmentChartInner
      data={data}
      eventId={eventId}
      eventStart={eventStart}
      eventEnd={eventEnd}
      massEventStart={massEventStart}
      massEventEnd={massEventEnd}
      eventMass={eventMass}
      massEventMass={massEventMass}
      area={targetArea}
      scaleReadingLookup={scaleReadingLookup}
      defaultVisibleStart={defaultVisibleStart}
      defaultVisibleEnd={defaultVisibleEnd}
      canZoomOut={hasWideMassAdjustmentView}
      storedInitialMass={storedInitialMass}
      storedFinalMass={storedFinalMass}
      pairedEventStart={pairedEventStart}
      pairedEventEnd={pairedEventEnd}
      playheadTime={playheadTime}
      onMassChange={onMassChange}
      onAutoSync={onAutoSync}
      onTimeAxisChange={onTimeAxisChange}
    />
  );
};
